import { formatVarTag, isRuleVarName } from "site/utils/rule-native-markup";

export type RuleEdit = {
  titleRu: string;
  titleEn: string;
  titleFr: string;
  refTitleAr: string;
  ru: string;
  en: string;
  fr: string;
  vars: Record<string, string>;
};

const RULE_VAR_RE = /\[\[([a-zA-Z_][a-zA-Z0-9_]*)\]\]/g;
const ARABIC_CHAR_RE = /[\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF]/;
const ARABIC_SPAN_RE = /[\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF][\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF\u064B-\u065F\u0670\u0640\u06D6-\u06ED\s]*/g;
const BRACE_HEAD_RE =
  /^(first\d+(?:\.[a-z][a-z0-9-]*)+|last\d+(?:\.[a-z][a-z0-9-]*)+|\d+(?:-\d+)?(?:\.[a-z][a-z0-9-]*)+|[a-z][a-z0-9.-]*|\.[a-z][a-z0-9.-]*)/i;

export function parseRuleVarNames(ru: string): string[] {
  const names: string[] = [];
  const seen = new Set<string>();
  RULE_VAR_RE.lastIndex = 0;
  let m: RegExpExecArray | null;
  while ((m = RULE_VAR_RE.exec(ru)) !== null) {
    const name = m[1]!;
    if (!isRuleVarName(name) || seen.has(name)) {
      continue;
    }
    seen.add(name);
    names.push(name);
  }
  return names;
}

export function removeRuleVarToken(ru: string, name: string): string {
  if (!name) {
    return ru;
  }
  const escaped = name.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  return ru.replace(new RegExp(` ?\\[\\[${escaped}\\]\\] ?`, "g"), " ");
}

export function insertRuleVarToken(ru: string, name: string, position?: number): string {
  const token = formatVarTag(name);
  if (position != null && position >= 0 && position <= ru.length) {
    const before = ru.slice(0, position);
    const after = ru.slice(position);
    const leftSep = before && !/\s$/.test(before) ? " " : "";
    const rightSep = after && !/^\s/.test(after) ? " " : "";
    return `${before}${leftSep}${token}${rightSep}${after}`;
  }
  const leftSep = ru && !/\s$/.test(ru) ? " " : "";
  return `${ru}${leftSep}${token}`;
}

export function syncRuleVarsWithText(ru: string, vars: Record<string, string>): { ru: string; vars: Record<string, string> } {
  const nextVars = { ...vars };
  for (const name of parseRuleVarNames(ru)) {
    if (!(name in nextVars)) {
      nextVars[name] = "";
    }
  }
  return { ru, vars: nextVars };
}

export function listRuleVarViews(ru: string, vars: Record<string, string>): Array<{ name: string; ar: string }> {
  const namesInRu = parseRuleVarNames(ru);
  const ordered = new Set<string>([...namesInRu, ...Object.keys(vars)]);
  return [...ordered].map((name) => ({ name, ar: vars[name] ?? "" }));
}

function readBraceBody(src: string, bodyStart: number): { body: string; end: number } | null {
  let depth = 1;
  for (let i = bodyStart; i < src.length; i += 1) {
    const ch = src[i];
    if (ch === "{") {
      depth += 1;
    } else if (ch === "}") {
      depth -= 1;
      if (depth === 0) {
        return { body: src.slice(bodyStart, i), end: i + 1 };
      }
    }
  }
  return null;
}

function replaceArabicSpans(
  text: string,
  nextVars: Record<string, string>,
  nextVarName: () => string,
): string {
  if (!ARABIC_CHAR_RE.test(text)) {
    return text;
  }
  return text.replace(ARABIC_SPAN_RE, (raw) => {
    const trail = raw.match(/\s*$/)?.[0] ?? "";
    const arabic = raw.slice(0, raw.length - trail.length).replace(/\s+/g, " ").trim();
    if (!arabic) {
      return raw;
    }
    for (const [name, value] of Object.entries(nextVars)) {
      if (value.trim() === arabic) {
        return `${formatVarTag(name)}${trail}`;
      }
    }
    const name = nextVarName();
    nextVars[name] = arabic;
    return `${formatVarTag(name)}${trail}`;
  });
}

function transformMarkupArabic(
  src: string,
  nextVars: Record<string, string>,
  nextVarName: () => string,
): string {
  const out: string[] = [];
  let index = 0;

  while (index < src.length) {
    const nextBrace = src.indexOf("{", index);
    const nextVar = src.indexOf("[[", index);
    const next =
      nextBrace === -1
        ? nextVar
        : nextVar === -1
          ? nextBrace
          : Math.min(nextBrace, nextVar);

    if (next === -1) {
      out.push(replaceArabicSpans(src.slice(index), nextVars, nextVarName));
      break;
    }

    if (next > index) {
      out.push(replaceArabicSpans(src.slice(index, next), nextVars, nextVarName));
    }

    if (next === nextVar) {
      const varMatch = src.slice(next).match(/^\[\[([^\]]+)\]\]/);
      if (!varMatch) {
        out.push(src[next]!);
        index = next + 1;
        continue;
      }
      out.push(varMatch[0]);
      index = next + varMatch[0].length;
      continue;
    }

    const headMatch = src.slice(next + 1).match(BRACE_HEAD_RE);
    if (!headMatch || src[next + 1 + headMatch[0].length] !== ":") {
      out.push(src[next]!);
      index = next + 1;
      continue;
    }

    const head = headMatch[1]!;
    const bodyStart = next + 1 + headMatch[0].length + 1;
    const wrapped = readBraceBody(src, bodyStart);
    if (!wrapped) {
      out.push(src[next]!);
      index = next + 1;
      continue;
    }

    const body = transformMarkupArabic(wrapped.body, nextVars, nextVarName);
    out.push(`{${head}:${body}}`);
    index = wrapped.end;
  }

  return out.join("");
}

export function autoInsertArabicVars(
  ru: string,
  vars: Record<string, string>,
): { ru: string; vars: Record<string, string> } {
  const nextVars: Record<string, string> = { ...vars };
  const usedNames = new Set<string>([...parseRuleVarNames(ru), ...Object.keys(nextVars)]);
  let counter = 1;
  const nextVarName = (): string => {
    while (usedNames.has(`var${counter}`)) {
      counter += 1;
    }
    const name = `var${counter}`;
    usedNames.add(name);
    counter += 1;
    return name;
  };

  return {
    ru: transformMarkupArabic(ru, nextVars, nextVarName),
    vars: nextVars,
  };
}

export function nextRuleVarName(ru: string): string {
  const existing = new Set(parseRuleVarNames(ru));
  let n = 1;
  while (existing.has(`var${n}`)) {
    n++;
  }
  return `var${n}`;
}

export function ruleDataToEdit(data: Record<string, unknown> | null | undefined): RuleEdit {
  const o = data ?? {};
  const vars: Record<string, string> = {};
  const rawVars = o.vars;
  if (rawVars && typeof rawVars === "object" && !Array.isArray(rawVars)) {
    for (const [key, value] of Object.entries(rawVars as Record<string, unknown>)) {
      if (typeof value === "string") {
        vars[key] = value;
      }
    }
  }

  return {
    titleRu: typeof o.titleRu === "string" ? o.titleRu : "",
    titleEn: typeof o.titleEn === "string" ? o.titleEn : "",
    titleFr: typeof o.titleFr === "string" ? o.titleFr : "",
    refTitleAr: typeof o.refTitleAr === "string" ? o.refTitleAr : "",
    ru: typeof o.ru === "string" ? o.ru : "",
    en: typeof o.en === "string" ? o.en : "",
    fr: typeof o.fr === "string" ? o.fr : "",
    vars,
  };
}

export function ruleEditToData(edit: RuleEdit): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  const titleRu = edit.titleRu.trim();
  const titleEn = edit.titleEn.trim();
  const titleFr = edit.titleFr.trim();
  const refTitleAr = edit.refTitleAr.trim();
  const ru = edit.ru;
  const en = edit.en;
  const fr = edit.fr;

  if (titleRu) {
    out.titleRu = titleRu;
  }
  if (titleEn) {
    out.titleEn = titleEn;
  }
  if (titleFr) {
    out.titleFr = titleFr;
  }
  if (refTitleAr) {
    out.refTitleAr = refTitleAr;
  }
  if (ru.trim()) {
    out.ru = ru;
  }
  if (en.trim()) {
    out.en = en;
  }
  if (fr.trim()) {
    out.fr = fr;
  }

  const names = parseRuleVarNames(ru);
  if (names.length) {
    const vars: Record<string, string> = {};
    for (const name of names) {
      const ar = (edit.vars[name] ?? "").trim();
      if (ar) {
        vars[name] = edit.vars[name] ?? "";
      }
    }
    if (Object.keys(vars).length) {
      out.vars = vars;
    }
  }

  return out;
}

export function isValidRuleId(ruleId: string): boolean {
  return /^[a-zA-Z0-9_]+$/.test(ruleId.trim());
}
